import { z } from "zod/v4";
import { createEndpointBase } from "./core";
import { CountResponseSchema, EndpointsSchemas } from "../ea.schema.endpoints";
import { booleanSchema, ScriptSchemas, TaskScriptSchema } from "../ea.schema";
import { GetRequestBaseResponseSchema, TaskScriptFilterSchema } from "../ea.schema.query";
import { LinkedMap } from "../ea.types";

const base = "scripts";
const scripts = createEndpointBase(base);

export const scriptsEndpoints = {
  scriptsGet: scripts({
    method: "GET",
    path: "",
    request: z.object({ filter: TaskScriptFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(ScriptSchemas["general"]),
    description: "Retrieves a list of automation scripts with filtering support.",
  }),
  scriptsSearch: scripts({
    method: "POST",
    path: "search",
    request: z.object({ filter: TaskScriptFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(ScriptSchemas["general"]),
    description:
      "Retrieves a list of automation scripts with filtering support via POST body. " +
      "Prefer over scriptsGet when the filter payload would produce a URL too long " +
      "for a safe GET request (e.g. large `id.in` arrays).",
  }),
  scriptsLinkedIn: scripts({
    method: "GET",
    path: ":id/linked-in",
    request: z.object({ filter: TaskScriptFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(ScriptSchemas["general"]),
    description: "Retrieves a list of scripts that link to the specified script.",
  }),
  scriptsDocumented: scripts({
    method: "GET",
    path: "documented",
    request: z.object({ filter: TaskScriptFilterSchema }).optional(),
    response: GetRequestBaseResponseSchema(ScriptSchemas["general"]),
    description: "Retrieves a list of documented automation scripts.",
  }),
  scriptsLinkedMap: scripts({
    method: "GET",
    path: ":id/linked-map",
    request: z.looseObject({}),
    response: z.any(),
    responseType: {} as LinkedMap,
    description: "Retrieves a map of scripts linked to the specified script.",
  }),
  scriptsGetOne: scripts({
    method: "GET",
    path: ":id",
    request: z.looseObject({}),
    response: ScriptSchemas["general"],
    description: "Retrieves a single automation script by ID.",
  }),
  scriptsCount: scripts({
    method: "GET",
    path: "count",
    request: z.any(),
    response: CountResponseSchema,
    description: "Returns the total number of automation scripts matching the filters.",
  }),
  scriptsCreate: scripts({
    method: "POST",
    path: "",
    request: ScriptSchemas["createData"],
    response: z.any(),
    description: "Creates a new automation script.",
  }),
  scriptsUpdate: scripts({
    method: "PATCH",
    path: ":id",
    request: z.object({
      script: ScriptSchemas["update"].extend({ id: z.number() }),
      options: z
        .object({
          credentials: z.object({ username: z.string(), password: z.string() }),
          confirmationToken: z.string(),
          versioning: z
            .object({ comment: z.string().optional(), tags: z.array(z.number()).optional() })
            .optional(),
          updateTestCaseStatus: z.boolean().optional(),
        })
        .partial()
        .optional(),
    }),
    response: z.any(),
    description: "Updates an existing automation script.",
  }),
  scriptsClose: scripts({
    method: "POST",
    path: "close",
    request: z.object({
      ids: z.array(z.number()),
      options: z
        .object({
          updateTestCaseStatus: z.boolean().optional(),
          confirmationToken: z.string(),
        })
        .partial()
        .optional(),
    }),
    response: z.any(),
    description: "Closes (deactivates) one or more automation scripts.",
  }),
  scriptsDelete: scripts({
    method: "DELETE",
    path: ":id",
    request: z.object({ id: z.number() }),
    response: z.any(),
    description: "Deletes an automation script.",
  }),
  scriptsAutocomplete: scripts({
    method: "GET",
    path: "autocomplete",
    request: z.object({ url: z.string() }),
    response: z.array(z.string()),
    description: "Retrieves URL hints for script autocompletion.",
  }),
  scriptsExport: scripts({
    method: "POST",
    path: "export",
    request: z.object({ scriptIds: z.array(z.coerce.number()) }),
    response: z.any(),
    description: "Exports specified scripts to a ZIP file.",
  }),
  scriptsImport: scripts({
    method: "POST",
    path: "import/:projectId",
    request: EndpointsSchemas.scripts.import.request,
    response: z.any(),
    description: "Imports scripts from uploaded files into a project.",
  }),
  scriptsConvertFromRSAT: scripts({
    method: "POST",
    path: "import/:projectId/rsat",
    request: z.object({}),
    response: z.any(),
    description: "Converts RSAT recording files (.axtr, .xml) into EA scripts.",
  }),
  scriptsClone: scripts({
    method: "POST",
    path: "clone",
    request: EndpointsSchemas.scripts.clone.request,
    response: TaskScriptSchema.array(),
    description: "Clones specified scripts to a project.",
  }),
  scriptsExportToCsv: scripts({
    method: "POST",
    path: "export-to-csv",
    request: z.object({
      additionalFields: z
        .object({
          takeScreenshots: z.boolean().optional(),
          screenshotsOnlyOnInterruption: z.boolean().optional(),
          clickOnDisabledButtons: z.boolean().optional(),
          useDefaultEnvironment: z.boolean().optional(),
          topProjectName: z.boolean().optional(),
          virtualUserName: z.boolean().optional(),
        })
        .optional(),
      modelName: z.literal("TaskScript"),
      filter: z.object({ select: z.looseObject({}).catchall(z.any()) }).catchall(z.any()),
    }),
    response: z.any(),
  }),
  scriptsChangeProject: scripts({
    path: "change-project",
    method: "POST",
    request: z.object({
      scriptIds: z.array(z.coerce.number()),
      data: ScriptSchemas["update"],
      options: z
        .object({
          credentials: z.object({ username: z.string(), password: z.string() }),
          versioning: z
            .object({ comment: z.string().optional(), tags: z.array(z.number()).optional() })
            .optional(),
          updateTestCaseStatus: booleanSchema.optional(),
        })
        .partial()
        .optional(),
    }),
    response: z.any(),
    description: "Moves specified scripts to a different project.",
  }),
  scriptsSummaryReportGenerate: scripts({
    method: "POST",
    path: "generate-summary-report",
    request: z.object({
      wsId: z.string(),
      projectId: z.coerce.number().optional(),
      query: TaskScriptFilterSchema.optional(),
      meta: z.object({
        additionalFields: z
          .object({
            scriptVersionNumber: z.boolean().optional(),
            listOfSteps: z.boolean().optional(),
            averageDuration: z.boolean().optional(),
            advancedConditional: z.boolean().optional(),
            summaryOfGlobalAndLocalVariables: z.boolean().optional(),
            changelog: z.boolean().optional(),
            linkedIn: z.boolean().optional(),
          })
          .optional(),
        customFilename: z.string().optional(),
      }),
    }),
    response: z.any(),
    description: "Generates a summary report for automation scripts.",
  }),
} as const;
